import React, { useEffect, useMemo, useState } from "react";
import { ref, onValue, update, remove } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { FileText, Trash2, CheckCircle, Loader2 } from "lucide-react";

const statusBadge = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "paid") return "bg-green-100 text-green-800";
  if (normalized === "overdue") return "bg-red-100 text-red-800";
  return "bg-yellow-100 text-yellow-800";
};

const CompanyInvoicesTab = ({ companyName }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);

  useEffect(() => {
    if (!companyName) return;
    const invoicesRef = ref(
      database,
      `Companies/${companyName}/ClientInvoices`
    );

    const unsubscribe = onValue(
      invoicesRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setInvoices([]);
          setLoading(false);
          return;
        }

        const data = snapshot.val() || {};
        const list = [];

        Object.entries(data).forEach(([clientId, clientInvoices]) => {
          const invs = clientInvoices?.invoices || clientInvoices;
          if (invs && typeof invs === "object") {
            Object.entries(invs).forEach(([id, invoice]) => {
              list.push({
                id,
                clientId,
                clientName:
                  invoice.clientName ||
                  invoice.client_id ||
                  invoice.ClientName ||
                  clientId,
                invoiceNumber: invoice.invoiceNumber || id,
                amount: Number(invoice.amount || 0),
                status: invoice.status || "unpaid",
                description: invoice.description || "",
                dueDate: invoice.dueDate || "",
                invoiceDate: invoice.invoiceDate || "",
                path: `Companies/${companyName}/ClientInvoices/${clientId}/invoices/${id}`,
              });
            });
          }
        });

        list.sort((a, b) => {
          const dateA = new Date(a.invoiceDate || 0);
          const dateB = new Date(b.invoiceDate || 0);
          return dateB - dateA;
        });

        setInvoices(list);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading company invoices:", error);
        toast.error("Failed to load invoices");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyName]);

  const stats = useMemo(() => {
    const pendingInvoices = invoices.filter(
      (inv) => (inv.status || "").toLowerCase() !== "paid"
    );
    return {
      total: invoices.length,
      pendingCount: pendingInvoices.length,
      pendingAmount: pendingInvoices.reduce(
        (sum, inv) => sum + Number(inv.amount || 0),
        0
      ),
      paidCount: invoices.filter(
        (inv) => (inv.status || "").toLowerCase() === "paid"
      ).length,
    };
  }, [invoices]);

  const handleMarkPaid = async (invoice) => {
    if (!invoice?.path) return;
    setWorkingId(invoice.id);
    try {
      await update(ref(database, invoice.path), {
        status: "paid",
        paidAt: new Date().toISOString(),
      });
      toast.success(`${invoice.invoiceNumber} marked as paid`);
    } catch (error) {
      console.error("Error marking invoice paid:", error);
      toast.error("Failed to mark as paid");
    } finally {
      setWorkingId(null);
    }
  };

  const handleDelete = async (invoice) => {
    if (!invoice?.path) return;
    if (
      typeof window !== "undefined" &&
      !window.confirm(`Remove ${invoice.invoiceNumber}? This cannot be undone.`)
    ) {
      return;
    }
    setWorkingId(invoice.id);
    try {
      await remove(ref(database, invoice.path));
      toast.success(`${invoice.invoiceNumber} removed`);
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to remove invoice");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          label="Total Invoices"
          value={stats.total}
          color="bg-cyan-50 text-cyan-800"
        />
        <SummaryCard
          label="Pending (count)"
          value={stats.pendingCount}
          color="bg-amber-50 text-amber-800"
        />
        <SummaryCard
          label="Pending Amount (KES)"
          value={stats.pendingAmount.toLocaleString()}
          color="bg-red-50 text-red-800"
        />
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <div className="p-4 border-b flex items-center gap-2">
          <FileText className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Invoices</h3>
          <span className="text-sm text-gray-500">
            Manage, mark paid, or remove sent invoices
          </span>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No invoices found for this company.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Invoice #</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Due Date</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-semibold text-gray-800">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="p-3 text-gray-700">
                    <div className="font-medium">{invoice.clientName}</div>
                    <div className="text-xs text-gray-500">{invoice.clientId}</div>
                  </td>
                  <td className="p-3 text-gray-800">
                    KES {Number(invoice.amount || 0).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(
                        invoice.status
                      )}`}
                    >
                      {invoice.status || "unpaid"}
                    </span>
                  </td>
                  <td className="p-3 text-gray-600">
                    {invoice.dueDate
                      ? new Date(invoice.dueDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="p-3 space-x-2">
                    {(invoice.status || "").toLowerCase() !== "paid" && (
                      <button
                        onClick={() => handleMarkPaid(invoice)}
                        disabled={workingId === invoice.id}
                        className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1 text-xs"
                      >
                        {workingId === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        Mark Paid
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(invoice)}
                      disabled={workingId === invoice.id}
                      className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1 text-xs"
                    >
                      {workingId === invoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div className={`rounded-lg p-4 border shadow-sm ${color}`}>
    <p className="text-sm font-medium">{label}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
  </div>
);

export default CompanyInvoicesTab;
